/**
 * LocalStorage utility wrapper for Brooks Flower
 * Handles cart persistence, order data, and other client-side storage needs
 */

const STORAGE_KEYS = {
  CART_ITEMS: 'bloombox_cart_items',
  CHECKOUT_DATA: 'bloombox_checkout_data',
  ORDER_HISTORY: 'bloombox_order_history',
  USER_PREFERENCES: 'bloombox_user_preferences',
};

class LocalStorageManager {
  /**
   * Safely get item from localStorage
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value if key doesn't exist
   * @returns {any} Parsed value or default
   */
  static get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading from localStorage key "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Safely set item in localStorage
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @returns {boolean} Success status
   */
  static set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
        console.warn('LocalStorage quota exceeded. Attempting to clear history to make room...');
        // Try clearing non-essential order history first
        OrderStorage.clear();

        // Try setting again
        try {
          localStorage.setItem(key, JSON.stringify(value));
          return true;
        } catch (retryError) {
          console.error('LocalStorage still full after clearing history.', retryError);
          return false;
        }
      }
      console.warn(`Error writing to localStorage key "${key}":`, error);
      return false;
    }
  }

  /**
   * Remove item from localStorage
   * @param {string} key - Storage key
   * @returns {boolean} Success status
   */
  static remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
      return false;
    }
  }

  /**
   * Clear all Brooks Flower related storage
   * @returns {boolean} Success status
   */
  static clear() {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
      return false;
    }
  }

  /**
   * Check if localStorage is available
   * @returns {boolean} Availability status
   */
  static isAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Cart-specific utilities
export const CartStorage = {
  /**
   * Get cart items from storage
   * @returns {Array} Cart items array
   */
  getItems() {
    return LocalStorageManager.get(STORAGE_KEYS.CART_ITEMS, []);
  },

  /**
   * Save cart items to storage
   * @param {Array} items - Cart items array
   * @returns {boolean} Success status
   */
  setItems(items) {
    return LocalStorageManager.set(STORAGE_KEYS.CART_ITEMS, items);
  },

  /**
   * Get cart total count
   * @returns {number} Total item count
   */
  getItemCount() {
    return this.getItems().reduce((total, item) => total + (item.quantity || 1), 0);
  },

  /**
   * Get cart total price
   * @returns {number} Total price
   */
  getTotalPrice() {
    return this.getItems().reduce((total, item) => {
      const price = item.price || 0;
      const quantity = item.quantity || 1;
      return total + (price * quantity);
    }, 0);
  }
};

// Checkout data utilities
export const CheckoutStorage = {
  /**
   * Get checkout form data
   * @returns {Object} Checkout data
   */
  getData() {
    return LocalStorageManager.get(STORAGE_KEYS.CHECKOUT_DATA, {
      customerInfo: {
        name: '',
        email: '',
        phone: '',
      },
      shippingAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Ethiopia',
      },
      notes: '',
    });
  },

  /**
   * Save checkout form data
   * @param {Object} data - Checkout data
   * @returns {boolean} Success status
   */
  setData(data) {
    return LocalStorageManager.set(STORAGE_KEYS.CHECKOUT_DATA, data);
  },

  /**
   * Update specific checkout field
   * @param {string} field - Field path (e.g., 'customerInfo.name')
   * @param {any} value - Field value
   * @returns {boolean} Success status
   */
  updateField(field, value) {
    const data = this.getData();
    const fieldParts = field.split('.');

    let current = data;
    for (let i = 0; i < fieldParts.length - 1; i++) {
      if (!current[fieldParts[i]]) {
        current[fieldParts[i]] = {};
      }
      current = current[fieldParts[i]];
    }

    current[fieldParts[fieldParts.length - 1]] = value;
    return this.setData(data);
  },

  /**
   * Clear checkout data
   * @returns {boolean} Success status
   */
  clear() {
    return LocalStorageManager.remove(STORAGE_KEYS.CHECKOUT_DATA);
  }
};

// Order history utilities
export const OrderStorage = {
  /**
   * Get order history
   * @returns {Array} Orders array
   */
  getOrders() {
    return LocalStorageManager.get(STORAGE_KEYS.ORDER_HISTORY, []);
  },

  /**
   * Add order to history
   * @param {Object} order - Order object
   * @returns {boolean} Success status
   */
  addOrder(order) {
    const orders = this.getOrders();
    const orderWithTimestamp = {
      ...order,
      id: order.id || `order_${Date.now()}`,
      createdAt: order.createdAt || new Date().toISOString(),
    };

    orders.unshift(orderWithTimestamp); // Add to beginning

    // Keep only last 50 orders to prevent storage bloat
    if (orders.length > 50) {
      orders.splice(50);
    }

    return LocalStorageManager.set(STORAGE_KEYS.ORDER_HISTORY, orders);
  },

  /**
   * Get order by ID
   * @param {string} orderId - Order ID
   * @returns {Object|null} Order object or null
   */
  getOrder(orderId) {
    const orders = this.getOrders();
    return orders.find(order => order.id === orderId) || null;
  },

  /**
   * Clear order history
   * @returns {boolean} Success status
   */
  clear() {
    return LocalStorageManager.remove(STORAGE_KEYS.ORDER_HISTORY);
  }
};

// User preferences utilities
export const PreferencesStorage = {
  /**
   * Get user preferences
   * @returns {Object} Preferences object
   */
  get() {
    return LocalStorageManager.get(STORAGE_KEYS.USER_PREFERENCES, {
      currency: 'ETB',
      language: 'en',
      notifications: true,
    });
  },

  /**
   * Set user preferences
   * @param {Object} preferences - Preferences object
   * @returns {boolean} Success status
   */
  set(preferences) {
    return LocalStorageManager.set(STORAGE_KEYS.USER_PREFERENCES, preferences);
  },

  /**
   * Update specific preference
   * @param {string} key - Preference key
   * @param {any} value - Preference value
   * @returns {boolean} Success status
   */
  update(key, value) {
    const preferences = this.get();
    preferences[key] = value;
    return this.set(preferences);
  }
};

export default LocalStorageManager;
export { STORAGE_KEYS };
